import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  async #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    const cart = await this.#getCart();
    const item = cart.items?.[line - 1];
    if (!item) return;

    // Bloquer tout changement direct sur la ligne gravure
    if (this.#isEngravingServiceItem(item)) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (lineItemRow) {
      const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
      textComponent?.shimmer();
    }

    this.#disableCartItems();
    const marker = cartPerformance.createStartingMarker('change:user-action');

    try {
      await this.#changeLineByKey(item.key, quantity);
      await this.#syncLinkedEngravingQuantity(item, quantity);
      await this.#refreshAllCartSections();
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(marker);
    }
  }

  async onLineItemRemove(line) {
    const cart = await this.#getCart();
    const itemToRemove = cart.items?.[line - 1];
    if (!itemToRemove) return;

    // Empêcher la suppression directe de la gravure seule
    if (this.#isEngravingServiceItem(itemToRemove)) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    this.#disableCartItems();
    const marker = cartPerformance.createStartingMarker('clear:user-action');

    try {
      // Supprimer le produit parent
      await this.#changeLineByKey(itemToRemove.key, 0);

      // Supprimer les gravures liées
      const cartAfterParentRemoval = await this.#getCart();
      const linkedItems = this.#getLinkedEngravingItems(cartAfterParentRemoval, itemToRemove);

      for (const linkedItem of linkedItems) {
        await this.#changeLineByKey(linkedItem.key, 0);
      }

      const finalCart = await this.#getCart();
      await this.#refreshAllCartSections(finalCart);

      if (!cartItemRowToRemove) return;

      const rowsToRemove = [
        cartItemRowToRemove,
        ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
      ];

      const isEmptyCart = finalCart.items.length === 0;

      const template = document.getElementById('empty-cart-template');
      if (isEmptyCart && template instanceof HTMLTemplateElement) {
        const clone = document.importNode(template.content, true);

        startViewTransition(() => {
          this.replaceChildren(clone);
        }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

        return;
      }

      rowsToRemove.forEach((row) => {
        const remove = () => row.remove();

        if (prefersReducedMotion()) return remove();

        row.style.setProperty('--row-height', `${row.clientHeight}px`);
        row.classList.add('removing');
        onAnimationEnd(row, remove);
      });
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(marker);
    }
  }

  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  async #getCart() {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`);
    if (!response.ok) throw new Error('Failed to load cart');
    return response.json();
  }

  #isEngravingServiceItem(item) {
    if (!item) return false;

    const serviceType = item.properties?._service_type;
    if (serviceType === 'gravure') return true;

    const title = String(item.product_title || item.title || '').trim().toLowerCase();

    return (
      title === 'personnalisation gravure' ||
      title === 'personnalisation gravure recto+verso'
    );
  }

  #getLinkedEngravingItems(cart, parentItem) {
    if (!cart?.items?.length || !parentItem) return [];

    return cart.items.filter((item) => {
      if (!this.#isEngravingServiceItem(item)) return false;

      const linkedProductKey = item.properties?._linked_product_key;
      const linkedProductKeyAlt = item.properties?._linked_product;
      const parentKey = item.parent_key;

      return (
        linkedProductKey === parentItem.key ||
        linkedProductKeyAlt === parentItem.key ||
        parentKey === parentItem.key
      );
    });
  }

  async #changeLineByKey(key, quantity) {
    const sectionsToUpdate = this.#getSectionsToUpdate();

    const body = JSON.stringify({
      id: key,
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(
      `${window.Shopify.routes.root}cart/change.js`,
      fetchConfig('json', { body })
    );

    const json = await response.json();

    if (!response.ok || json.errors) {
      throw new Error(json.errors || 'Cart change failed');
    }

    return json;
  }

  async #syncLinkedEngravingQuantity(parentItem, quantity) {
    const cart = await this.#getCart();
    const linkedItems = this.#getLinkedEngravingItems(cart, parentItem);

    for (const linkedItem of linkedItems) {
      await this.#changeLineByKey(linkedItem.key, quantity);
    }
  }

  async #refreshAllCartSections(existingCart = null) {
    const parsedResponseText = existingCart || (await this.#getCart());

    const sectionsToUpdate = this.#getSectionsToUpdate();
    const sectionResponse = await fetch(
      `${window.Shopify.routes.root}?sections=${Array.from(sectionsToUpdate).join(',')}`
    );
    const sections = await sectionResponse.json();

    this.#updateQuantitySelectors(parsedResponseText);

    this.dispatchEvent(
      new CartUpdateEvent(
        { ...parsedResponseText, sections },
        this.sectionId,
        {
          itemCount: parsedResponseText.item_count ?? 0,
          source: 'cart-items-component',
          sections,
        }
      )
    );

    if (sections[this.sectionId]) {
      morphSection(this.sectionId, sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    this.#updateCartQuantitySelectorButtonStates();
  }

  #getSectionsToUpdate() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    return sectionsToUpdate;
  }

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }

  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}